/** */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAnalystService } from 'hooks/api/agents/analyst';
import GmModal from 'app/shared/modal/modal';
import { InsightList } from 'app/shared/insight-list';
import { Button } from 'app/shared/button';
import styles from './board.module.css';

const STAGES = [
  { key: 'new', label: 'New', headerClass: 'new', scoreClass: 'scoreNew' },
  { key: 'onboarded', label: 'Onboarded', headerClass: 'onboarded', scoreClass: 'scoreOnboarded' },
  { key: 'healthy', label: 'Healthy', headerClass: 'healthy', scoreClass: 'scoreHealthy' },
  { key: 'at_risk', label: 'At Risk', headerClass: 'atRisk', scoreClass: 'scoreAtRisk' },
  { key: 'critical', label: 'Critical', headerClass: 'critical', scoreClass: 'scoreCritical' }
];

const RISK_LABELS = {
  new: 'New',
  onboarded: 'Onboarded',
  healthy: 'Healthy',
  at_risk: 'At Risk',
  critical: 'Critical'
};

const scoreColor = (score) => {
  if (score == null) return 'var(--neutral-dark-4)';
  if (score >= 80) return 'var(--green-primary)';
  if (score >= 60) return 'var(--orange-primary)';
  return 'var(--red-primary)';
};

const formatMrr = (mrr) => {
  if (!mrr && mrr !== 0) return null;
  return mrr >= 1000 ? `$${(mrr / 1000).toFixed(1)}k` : `$${mrr}`;
};

const formatLastActive = (iso) => {
  if (!iso) return '—';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  return `${days}d ago`;
};

const TenantSnapshotModal = ({ tenant, onClose, onNavigate }) => {
  const score = tenant.health_score;
  const color = scoreColor(score);
  const pct = Math.min(100, Math.max(0, score ?? 0));
  const mrr = formatMrr(tenant.mrr);

  return (
    <div className={styles.modalBody}>
      <div className={styles.snapshotHeader}>
        <div className={styles.snapshotName}>{tenant.tenant_name}</div>
        {tenant.risk_level && (
          <span className={`${styles.riskBadge} ${styles[`badge_${tenant.risk_level}`]}`}>
            {RISK_LABELS[tenant.risk_level] || tenant.risk_level}
          </span>
        )}
      </div>

      <div className={styles.healthWrap}>
        <span className={styles.healthScore} style={{ color }}>
          {score ?? '—'}
        </span>
        <div className={styles.healthRight}>
          <div className={styles.healthTrack}>
            <div className={styles.healthFill} style={{ width: `${pct}%`, background: color }} />
          </div>
          <span className={styles.healthLabel}>health score</span>
        </div>
      </div>

      <div className={styles.metaRow}>
        {mrr && (
          <div className={styles.metaItem}>
            <span className={styles.metaLabel}>MRR</span>
            <span className={styles.metaValue}>{mrr}</span>
          </div>
        )}
        {tenant.plan && (
          <div className={styles.metaItem}>
            <span className={styles.metaLabel}>Plan</span>
            <span className={styles.metaValue}>{tenant.plan}</span>
          </div>
        )}
        <div className={styles.metaItem}>
          <span className={styles.metaLabel}>Last active</span>
          <span className={styles.metaValue}>{formatLastActive(tenant.last_active_at)}</span>
        </div>
      </div>

      <div className={styles.modalDivider} />

      <div className={styles.suggestionsSection}>
        <span className={styles.suggestionsLabel}>Signals & Suggestions</span>
        <InsightList insights={tenant.signals || []} empty="No signals recorded for this tenant." />
      </div>

      <div className={styles.modalFooter}>
        <Button text="View full dashboard" onClick={onNavigate} />
      </div>
    </div>
  );
};

const TenantCard = ({ tenant, scoreClass, onSelect }) => {
  const mrr = formatMrr(tenant.mrr);

  return (
    <div className={styles.card} onClick={() => onSelect(tenant)}>
      <div className={styles.cardHeader}>
        <span className={styles.tenantName}>{tenant.tenant_name}</span>
        <span className={`${styles.score} ${styles[scoreClass]}`}>
          {tenant.health_score ?? '—'}
        </span>
      </div>
      {tenant.signals?.[0] && <div className={styles.signal}>{tenant.signals[0]}</div>}
      <div className={styles.cardMeta}>
        {tenant.plan && <span className={styles.plan}>{tenant.plan}</span>}
        {mrr && <span className={styles.mrr}>{mrr}</span>}
      </div>
    </div>
  );
};

export const TenantBoard = () => {
  const { fetchTenants } = useAnalystService();
  const navigateTo = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected_tenant, setSelectedTenant] = useState(null);

  useEffect(() => {
    fetchTenants({ query_string: 'population=200' }).then(({ tenants: t }) => {
      setTenants(t || []);
      setLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <div className={styles.loading}>Loading board…</div>;

  const grouped = STAGES.reduce((acc, stage) => {
    acc[stage.key] = tenants.filter((t) => t.risk_level === stage.key);
    return acc;
  }, {});

  return (
    <>
      <div className={styles.board}>
        {STAGES.map((stage) => (
          <div key={stage.key} className={styles.column}>
            <div className={`${styles.columnHeader} ${styles[stage.headerClass]}`}>
              <span className={styles.columnLabel}>{stage.label}</span>
              <span className={styles.columnCount}>{grouped[stage.key].length}</span>
            </div>
            <div className={styles.cards}>
              {grouped[stage.key].length === 0 ? (
                <div className={styles.empty}>No tenants</div>
              ) : (
                grouped[stage.key].map((t) => (
                  <TenantCard
                    key={t.tenant_id}
                    tenant={t}
                    scoreClass={stage.scoreClass}
                    onSelect={setSelectedTenant}
                  />
                ))
              )}
            </div>
          </div>
        ))}
      </div>

      <GmModal
        title={selected_tenant?.tenant_name || ''}
        show_title
        show_modal={!!selected_tenant}
        onClose={() => setSelectedTenant(null)}
      >
        {selected_tenant && (
          <TenantSnapshotModal
            tenant={selected_tenant}
            onClose={() => setSelectedTenant(null)}
            onNavigate={() => navigateTo(`/tenants/${selected_tenant.tenant_id}/dashboard`)}
          />
        )}
      </GmModal>
    </>
  );
};
